import { UUID } from "@bitory-gmbh/nidavellir";
import { documentDbPut } from "@/shared/lib/storage/indexdb/document-db";
import { GSMDocumentKind } from "@/services/backend/documents/types/kind";
import t from "@/lang/lang";
import {
  Query,
  useDocumentDBGetAll,
} from "@/shared/lib/storage/indexdb/document-db-hooks";
import {
  useHtzOfferListQuery,
  useLazyHtzOfferListQuery,
} from "@/services/backend/htz/offers/api/offer-api";
import { Offer } from "@/services/backend/htz/offers/types/offer";
import { useParams } from "react-router";
import { useStaleContext } from "./stale-context";
import { OfflineState, useOfflineContext } from "./offline-context";

export function useOffersOffline(workOrderId: UUID, processId: string) {
  const { setSynced } = useOfflineContext();
  const { addStale } = useStaleContext();
  const [load] = useLazyHtzOfferListQuery();

  const prepMsg = t("Speichere Angebote ...");
  const doneMsg = t("Angebote");

  const prepare = async () => {
    const result = await load({
      process: { active: true, values: [processId] },
      immutable: { active: true, values: [true] },
    });
    if (result.error) throw result.error;

    addStale("offers");

    setSynced((prev) => [...prev, prepMsg]);

    await Promise.all(
      (result.data!.items ?? []).map(async (item) => {
        await documentDbPut<Offer>(
          GSMDocumentKind.WorkOrder,
          workOrderId,
          "offers",
          item,
        );
      }),
    );

    setSynced((prev) => prev.map((p) => (p === prepMsg ? doneMsg : p)));
  };

  return { prepare };
}

export function useGetOffers(processId: string): Query<Offer[]> {
  const { offlineState } = useOfflineContext();
  const { workOrderId } = useParams();

  const {
    data: serverData,
    isLoading: serverIsLoading,
    error: serverError,
  } = useHtzOfferListQuery({
    process: { active: true, values: [processId] },
    immutable: { active: true, values: [true] },
  });

  const {
    data: indexedDBData,
    isLoading: indexedDBIsLoading,
    error: indexedDBError,
  } = useDocumentDBGetAll<Offer>(
    GSMDocumentKind.WorkOrder,
    workOrderId!,
    "offers",
  );

  switch (offlineState) {
    case OfflineState.ONLINE:
      return {
        data: serverData?.items,
        isLoading: serverIsLoading,
        error: serverError,
      };
    case OfflineState.OFFLINE:
      return {
        data: indexedDBData,
        isLoading: indexedDBIsLoading,
        error: indexedDBError,
      };
    default:
      return {
        data: undefined,
        isLoading: false,
        error: undefined,
      };
  }
}
